import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../redux/store';
import { fetchAlbums } from '../redux/thunks/albumThunks';
import AlbumCard from './AlbumCard';
import Sidebar from './Sidebar';
import Header from './Header';
import NavbarApp from './navbarApp';
import { ImageIcon } from 'lucide-react';

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const albumState = useSelector((state: RootState) => state.albums);
  const authState = useSelector((state: RootState) => state.auth);

  const user = authState.user;
  const albums = albumState.list.albums || [];
  const filteredAlbums = albums.length === 1
  ? albums
  : albums.filter(album => album.title.toLowerCase() !== 'demo');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (user?.id && albumState.status === 'idle') {
      dispatch(fetchAlbums({ customerId: user.id }));
    }
  }, [dispatch, user, albumState.status]);

  if (authState.loading || albumState.status === 'loading') {
    return <p className="text-center mt-10 text-gray-500">Loading...</p>;
  }

  if (authState.error || albumState.status === 'failed') {
    return (
      <p className="text-center mt-10 text-red-700">
        Error: {albumState.errorMessage || ''} {authState.error || ''}
      </p>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 text-gray-800">
      {/* Top navbar with toggle */}
      <NavbarApp user={user} setSidebarOpen={setSidebarOpen}/>

      {/* Mobile Sidebar overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div
          className="absolute inset-0 bg-gray-800 bg-opacity-50"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="relative w-64 h-full bg-gray-100 border-r border-rose-300">
          <Sidebar />
        </div>
      </div>

      {/* Content below navbar: Sidebar (md+) + Main */}
      <div className="flex flex-1">
        {/* Sidebar for desktop */}
        <div className="hidden md:block w-64 bg-gray-100 border-r border-rose-300">
          <Sidebar />
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-y-auto">
          <Header />
          <div className="px-4 sm:px-8 py-6">
            <h2 className="text-2xl font-bold mb-6 text-red-700">
              Your Albums
            </h2>
            {albums.length === 0 ? (
              <div className="flex flex-col items-center justify-center mt-10 space-y-4 py-16 rounded-2xl border border-rose-300 bg-white">
                <ImageIcon className="w-12 h-12 text-red-500" />
                <p className="text-gray-800">No albums created yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredAlbums.map((album) => (
                  <AlbumCard
                    key={album.albumId}
                    albumId={album.albumId}
                    title={album.title}
                    category={album.thumbnail}
                    tags={album.tags}
                    createdBy={album.createdBy}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
